import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  TextField,
} from '@mui/material';
import { IconPhone } from '@tabler/icons-react';
import { useSnackbar } from 'notistack';
import { useCreateDemoRequest } from '@/lib/api/demo-requests';
import { useGetMe } from '@/lib/api/me';

interface DemoRequestModalProps {
  open: boolean;
  onClose: () => void;
}

export const DemoRequestModal: React.FC<DemoRequestModalProps> = ({ open, onClose }) => {
  const { data: me } = useGetMe();
  const [phone, setPhone] = useState(me?.phone ?? '');
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (open && me?.phone) setPhone(me.phone);
  }, [open, me?.phone]);

  const { enqueueSnackbar } = useSnackbar();
  const mutation = useCreateDemoRequest();

  const handleSubmit = async () => {
    if (!phone) return;

    try {
      await mutation.mutateAsync({ phone, comments: comments || undefined });
      enqueueSnackbar('¡Solicitud enviada! Nos pondremos en contacto contigo pronto.', {
        variant: 'success',
        autoHideDuration: 6000,
      });
      handleClose();
    } catch {
      enqueueSnackbar('Ha ocurrido un error. Inténtalo de nuevo.', { variant: 'error' });
    }
  };

  const handleClose = () => {
    setPhone('');
    setComments('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <IconPhone size={22} />
            Solicitar demo personalizada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Déjanos tu teléfono y nos ponemos en contacto contigo para mostrarte la plataforma.
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            label="Teléfono"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="600 000 000"
            fullWidth
            required
            autoFocus
          />

          <TextField
            label="Comentarios (opcional)"
            multiline
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Cuéntanos qué te interesa ver, tu perfil (inversor / PSI), dudas..."
            fullWidth
            inputProps={{ maxLength: 500 }}
            helperText={`${comments.length}/500`}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          disabled={mutation.isPending}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!phone || mutation.isPending}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3 }}
        >
          {mutation.isPending ? 'Enviando...' : 'Solicitar demo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
